import React from "react";

export const isMobileView = () => window.innerWidth < 62 * 16; //62 rem * 16px

export function formatAddress(
  address: string,
  length = 12,
  onlyMobile = false,
  longerStart = false
): string {
  if ((!isMobileView() && onlyMobile) || address.length <= length) {
    return address;
  }

  // if its mobile view show first 3 and last 3 characters
  if (isMobileView()) {
    return (
      address.substring(0, 3) + "..." + address.substring(address.length - 3)
    );
  }

  // Full length is 12 characters
  const halfLength = Math.floor(length / 2);
  const start = address.substring(0, halfLength + 4 * Number(longerStart));
  const end = address.substring(address.length - halfLength);

  return start + "..." + end;
}

/**
 * Remove _TX from transaction type values and replace _ with space " "
 * @param {string} type
 */
export function formatTransactionType(type: string): string {
  if (type.endsWith("_TX")) {
    return type.slice(0, -3).replace("_", " ").toLowerCase();
  }
  return type.toLowerCase();
}

export function transformToLowerExceptFirst(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

/**
 * Formats the amount with FLR
 * @param amount
 * @param convert convert from gwei
 * @returns {React.ReactNode}
 */
export const formatAmountWithLabel = (
  amount: number,
  convert = true,
  digits?: number
): React.ReactNode => {
  return (
    <>
      <span>{formatNumber(amount, convert, digits)}</span>
      &nbsp;
      <span style={{ fontWeight: 400 }}>FLR</span>
    </>
  );
};

export function convertFromGwei(amount: number): number {
  return amount / 1e9;
}

export function formatNumber(
  amount: number,
  convert = true,
  digits?: number
): string {
  const converted = convert ? convertFromGwei(amount) : amount;
  return new Intl.NumberFormat(Intl.NumberFormat().resolvedOptions().locale, {
    maximumFractionDigits: digits ?? 20,
    minimumFractionDigits: digits ?? 2,
  }).format(converted);
}

/**
 * Parse a localized number to a float.
 * @param {string} stringNumber - the localized number
 * @param {string} locale - [optional] the locale that the number is represented in. Omit this parameter to use the current locale.
 */
export function parseLocaleNumber(
  stringNumber: string,
  locale?: string
): number {
  const thousandSeparator: string = Intl.NumberFormat(locale).format(11111);
  // .replace(/\p{Number}/gu, ""); //TODO: Find alternative for unsupported regex sign "u"
  const decimalSeparator: string = Intl.NumberFormat(locale).format(1.1);
  // .replace(/\p{Number}/gu, "");

  return parseFloat(
    stringNumber
      .replace(new RegExp("\\" + thousandSeparator, "g"), "")
      .replace(new RegExp("\\" + decimalSeparator), ".")
  );
}

export function getYearlyROI(epochPercent: number): number {
  return (epochPercent / 3.5) * 365;
}

// The backend returns the yearly APR as a fraction (e.g. 0.29 = 29%) and has no
// guard of its own. Providers with a very low normalised weight can occasionally
// yield an absurd APR (rewards divided by a near-zero weight), which would
// otherwise render as e.g. "6.4e24%". Anything above this ceiling (= 100,000%)
// is treated as an unreliable outlier and hidden from the user.
export const MAX_REASONABLE_YEARLY_APR = 1000; // fraction, i.e. 100,000%

export function isOutlierYearlyApr(
  yearlyApr: number | null | undefined
): boolean {
  return (
    yearlyApr === null ||
    yearlyApr === undefined ||
    !Number.isFinite(yearlyApr) ||
    yearlyApr > MAX_REASONABLE_YEARLY_APR
  );
}
